/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useState } from 'react';
import { Card, Col, Modal, Row, Table } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { DoughnutChart } from 'components/charts';
import { Button } from 'atoms';
import { LoadingAnimation } from 'components/common';

const CardContainer = styled(Card)`
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const TitleText = styled.div`
	font-weight: 500;
	font-size: 16px;
`;

const ViewMore = styled(Button)`
	color: #ee6055;
	float: right;
	background: #fff6f5;
`;

const ChartWRapper = styled.div`
	height: 120px;
`;

const EmptyChart = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 30px;
	font-weight: 500;
	min-height: 95px;
`;

const columns = [
	{
		title: 'Vehicle Type',
		dataIndex: 'vehicle_type',
		key: 'vehicle_type',
	},
	{
		title: 'Vehicle Count',
		dataIndex: 'vehicle_count',
		key: 'vehicle_count',
	},
	{
		title: 'Percentage',
		dataIndex: 'percent',
		key: 'percent',
	},
];

function TotalVehicles({ vehicles, isLoading }) {
	const [modalVisible, setModalVisible] = useState(false);
	const [totalVehiclesList, setTotalVehiclesList] = useState(null);
	const [donutList, setDonutList] = useState();
	const showModal = () => {
		setModalVisible(true);
	};

	const handleCancel = () => {
		setModalVisible(false);
	};

	useEffect(() => {
		if (vehicles?.vehicles && vehicles?.total) {
			const getList = vehicles.vehicles;
			if (getList.length > 0) {
				setTotalVehiclesList(getList);
				const getDonutListValue = getList.length > 3 ? getList.slice(0, 3) : getList;
				const sumGetDonutListValue =
					100 -
					getDonutListValue.map((res1) => res1.percent).reduce((acc, cur) => acc + cur);

				if (getList.length > 3) {
					const otherCount = getList
						.slice(3, getList.length)
						.map((res3) => res3.vehicle_count)
						.reduce((total, item) => total + item, 0);
					const finalDonutList = [
						...getDonutListValue,
						{
							percent: sumGetDonutListValue,
							vehicle_count: otherCount,
							vehicle_type: 'Others',
						},
					];

					setDonutList(finalDonutList);
				} else {
					setDonutList(getDonutListValue);
				}
			}
		} else {
			setDonutList(null);
			setTotalVehiclesList(null);
		}
	}, [vehicles]);

	const donutData = {
		responsive: true,
		maintainAspectRatio: false,
		labels: donutList && donutList.map((res) => `${res.vehicle_type} ${res.percent}%`),
		datasets: [
			{
				totalVehicles: donutList && donutList.map((res) => res.vehicle_count),
				label: 'Total Vehicles',
				data: donutList && donutList.map((res) => res.percent),
				backgroundColor: ['#EE6055', '#A1EF7A', '#70D6FF', '#9381FF'],
				hoverOffset: 4,
			},
		],
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			datalabels: {
				color: 'white',
				font: {
					size: '13%',
					weight: 500,
				},
			},
			legend: {
				position: 'right',
				labels: {
					boxWidth: 15,
					boxHeight: 15,
				},
				onClick: (e) => {
					// eslint-disable-next-line vars-on-top, no-var, no-redeclare
					if (!e) var e = window.event;
					e.cancelBubble = true;
					if (e.stopPropagation) e.stopPropagation();
				},
			},
			tooltip: {
				enabled: true,
				usePointStyle: true,
				callbacks: {
					// To change title in tooltip
					// eslint-disable-next-line arrow-body-style
					title: (data) => {
						return data[0].parsed.x;
					},

					// To change label in tooltip
					// eslint-disable-next-line arrow-body-style
					label: (data) => {
						return ` No of Vehicle: ${data.dataset.totalVehicles[data.dataIndex]}`;
					},
				},
			},
		},
	};

	return (
		<CardContainer>
			{isLoading ? (
				<LoadingAnimation visible={isLoading} animationID="TotalVehiclesLoader" />
			) : (
				// eslint-disable-next-line react/jsx-no-useless-fragment
				<>
					<Row align="stretch" justify="space-between">
						{donutList ? (
							<>
								<Col xs={24} sm={12} md={12}>
									<TitleText>Total Vehicles</TitleText>
								</Col>
								<Col xs={24} sm={12} md={12}>
									<ViewMore onClick={showModal} overflow="auto">
										View More
										<EyeOutlined style={{ marginLeft: '10px' }} />
									</ViewMore>
								</Col>
								<Col xs={24} sm={24} md={24}>
									<ChartWRapper>
										<DoughnutChart
											ChartOptions={chartOptions}
											chartData={donutData}
										/>
									</ChartWRapper>
								</Col>
								{totalVehiclesList && totalVehiclesList.length > 0 && (
									<Modal
										title="Total Vehicles"
										open={modalVisible}
										onCancel={handleCancel}
										footer={null}
										bodyStyle={{ maxHeight: '500px', overflow: 'auto' }}
									>
										<Table
											dataSource={totalVehiclesList}
											columns={columns}
											pagination={false}
											scroll={{ y: 240 }}
										/>
									</Modal>
								)}
							</>
						) : (
							<>
								<Col xs={24} sm={24} md={24}>
									<TitleText>Total Vehicles</TitleText>
								</Col>
								<Col xs={24} sm={24} md={24}>
									<EmptyChart>0</EmptyChart>
								</Col>
							</>
						)}
					</Row>
				</>
			)}
		</CardContainer>
	);
}

TotalVehicles.propTypes = {
	vehicles: PropTypes.object,
	isLoading: PropTypes.bool,
};

export default TotalVehicles;
